import { useEffect, useState } from "react";
import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import CampaignTabs from "../../components/CampaignTabs";
import WaitTab from "./WaitTab";
import InprogressTab from "./InprogressTab";
import CompleteTab from "./CompleteTab";
import ReusableSearchBar from "../../components/ReusableSearchBar";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ResultClosing, ResultOngoing, ResultWating } from "../../api";

export default function ResultPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 탭 → API 매핑
  const apiMap = {
    wait: ResultWating,
    inprogress: ResultOngoing,
    complete: ResultClosing,
  };

  const currentTab = Object.keys(apiMap).find((key) => pathname.includes(key));

  // 리스트 상태
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState();

  const [keyword, setKeyword] = useState("");
  const [billingFilter, setBillingFilter] = useState("ALL");

  // 공통 API 호출 함수
  const loadItems = async () => {
    try {
      setLoading(true);

      const apiFn = apiMap[currentTab];
      if (!apiFn) return;

      const res = await apiFn();

      setCounts(res?.data?.counts);
      setItems(res?.data?.items ?? []); // 반드시 추가!
    } catch (e) {
      console.error("loadItems error:", e);
    } finally {
      setLoading(false);
    }
  };

  // 탭 변경 시 초기화 + 첫 페이지 로드
  useEffect(() => {
    setItems([]);
    setKeyword("");
    setBillingFilter("ALL");

    loadItems();
    // eslint-disable-next-line
  }, [currentTab]);

  const billingOf = (item) =>
    String(item?.campaign?.billingModel ?? "").toUpperCase();

  const billingCounts = items.reduce(
    (acc, item) => {
      const billing = billingOf(item);
      acc.ALL += 1;
      if (acc[billing] != null) acc[billing] += 1;
      return acc;
    },
    { ALL: 0, CPUC: 0, CPUR: 0, CPS: 0 }
  );

  const billingFiltered =
    billingFilter === "ALL"
      ? items
      : items.filter((item) => billingOf(item) === billingFilter);

  const filtered = billingFiltered
    .map((item) => {
      const creators = item.creators?.filter((c) => {
        const username = c.creator?.username?.toLowerCase() ?? "";
        const title = item.campaign?.title?.toLowerCase() ?? "";
        return (
          username.includes(keyword.toLowerCase()) ||
          title.includes(keyword.toLowerCase())
        );
      });

      if (!creators || creators.length === 0) return null;
      return { ...item, creators };
    })
    .filter(Boolean);

  const campaignProps = {
    list: filtered,
    loading,
  };

  const tabs = [
    { label: `대기중 (${counts?.waiting ?? 0})`, path: "wait" },
    { label: `진행중 (${counts?.ongoing ?? 0})`, path: "inprogress" },
    { label: `진행 완료 (${counts?.closing ?? 0})`, path: "complete" },
  ];
  const activeIndex = tabs.findIndex((t) => pathname.includes(t.path));
  const onTabChange = (index) => {
    navigate(`/result/${tabs[index].path}`);
  };

  const billingTabs = [
    { label: "전체", value: "ALL" },
    { label: "CPUC", value: "CPUC" },
    { label: "CPUR", value: "CPUR" },
    { label: "CPS", value: "CPS" },
  ];

  const title = [
    "광고 시작 대기 목록",
    "광고 진행 목록",
    "광고 진행 완료 목록",
  ];
  const desc = [
    "콘텐츠가 승인되어 광고 게시 준비중인 목록입니다.",
    "현재 광고가 진행 중인 캠페인의 노출, 클릭 등 성과를 확인할 수 있습니다.",
    "광고가 종료된 캠페인의 최종 성과를 확인할 수 있습니다.",
  ];

  return (
    <Flex flexDir="column" h="100%" overflow="hidden">
      <Flex justify="space-between" alignItems="center" mb="24px">
        <CampaignTabs
          tabLabels={tabs.map((t) => t.label)}
          onChange={onTabChange}
          activeIndex={activeIndex}
        />
        <ReusableSearchBar
          placeholder="정산 검색"
          width="229px"
          onSearch={(v) => setKeyword(v)}
        />{" "}
      </Flex>
      <Box mb="16px">
        <Text fontSize="xl" fontWeight="bold" mb="2">
          {title[activeIndex]}
        </Text>
        <Text color="gray.600" mb="6">
          {desc[activeIndex]}
        </Text>
        <HStack spacing="8px" flexWrap="wrap">
          {billingTabs.map((tab) => {
            const active = billingFilter === tab.value;
            return (
              <Button
                key={tab.value}
                size="sm"
                h="34px"
                px="14px"
                borderRadius="8px"
                bg={active ? "#0054C0" : "white"}
                color={active ? "white" : "#172940"}
                border="1px solid"
                borderColor={active ? "#0054C0" : "#D9E1EC"}
                fontWeight="700"
                _hover={{
                  bg: active ? "#0045A0" : "#F5F9FF",
                  borderColor: "#0054C0",
                }}
                onClick={() => setBillingFilter(tab.value)}
              >
                {tab.label} ({billingCounts[tab.value] ?? 0})
              </Button>
            );
          })}
        </HStack>
      </Box>

      <Box flex="1" overflowY="auto">
        <Routes>
          <Route path="wait/*" element={<WaitTab {...campaignProps} />} />
          <Route
            path="inprogress/*"
            element={<InprogressTab {...campaignProps} />}
          />
          <Route
            path="complete/*"
            element={<CompleteTab {...campaignProps} />}
          />
          <Route path="*" element={<Navigate to="wait" replace />} />
        </Routes>
      </Box>
    </Flex>
  );
}
